<ng-container *transloco="let t">
  <p-confirmdialog />

  <!-- Use flex with column direction and set a fixed height -->
  <div class="flex flex-col h-full max-h-[80vh]">
    <!-- Content section - scrollable -->
    <!-- Right-side padding compensates for the scrollbar width -->
    <div class="flex-1 overflow-y-auto pr-3">
      <div class="mb-5">
        <p class="dark:text-surface-400">
          {{
            t(
              isCreateMode
                ? "app.organization-settings.editAccessRoleDialog.createDescription"
                : "app.organization-settings.editAccessRoleDialog.description"
            )
          }}
        </p>
      </div>
      <div class="flex flex-col gap-5" [formGroup]="form">
        @if (accessRole?.isBuiltInRole) {
          <div
            class="flex gap-3 p-3 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-md"
          >
            <i class="pi pi-info-circle text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"></i>
            <p class="text-sm text-surface-700 dark:text-surface-300">
              {{ t("app.organization-settings.accessRoles.builtInRoleHint") }}
            </p>
          </div>
        }
        <!-- Access Role Name -->
        <div class="flex flex-col gap-2">
          <div class="flex flex-row gap-1">
            <label for="accessRoleName">{{ t("app.organization-settings.editAccessRoleDialog.form.nameLabel") }}</label>
            <span class="text-red-500">*</span>
          </div>
          <input pInputText id="accessRoleName" formControlName="name" />
          @if (form.controls.name.invalid && form.controls.name.touched) {
            <small class="text-red-500">{{
              t("app.organization-settings.editAccessRoleDialog.form.nameRequired")
            }}</small>
          }
        </div>

        <!-- Access Role Color -->
        <div class="flex flex-col gap-2">
          <label for="color">{{ t("app.organization-settings.editAccessRoleDialog.form.colorLabel") }}</label>
          <p-select
            id="color"
            formControlName="color"
            [options]="accessRoleColorOptions"
            optionLabel="label"
            optionValue="value"
          >
            <ng-template #selectedItem let-selectedOption>
              <p-tag [severity]="selectedOption.severity" [value]="selectedOption.label" />
            </ng-template>
            <ng-template let-color #item>
              <p-tag [severity]="color.severity" [value]="color.label" />
            </ng-template>
          </p-select>
        </div>

        <p-divider styleClass="my-0" />

        <!-- Organization Permissions -->
        <div class="flex flex-col gap-4">
          <p class="text-lg font-medium">
            {{ t("app.organization-settings.editAccessRoleDialog.form.organizationPermissions") }}
          </p>

          <div class="flex items-center gap-1">
            <p-checkbox inputId="canAcceptTos" [binary]="true" formControlName="canAcceptTos" />
            <label for="canAcceptTos" class="ml-2">{{
              t("app.organization-settings.editAccessRoleDialog.form.canAcceptTos")
            }}</label>
          </div>
          <div class="flex items-center gap-1">
            <p-checkbox inputId="canModifyAccessRoles" [binary]="true" formControlName="canModifyAccessRoles" />
            <label for="canModifyAccessRoles" class="ml-2">{{
              t("app.organization-settings.editAccessRoleDialog.form.canModifyAccessRoles")
            }}</label>
          </div>
          <div class="flex items-center gap-1">
            <p-checkbox inputId="canModifyApprovalGroups" [binary]="true" formControlName="canModifyApprovalGroups" />
            <label for="canModifyApprovalGroups" class="ml-2">{{
              t("app.organization-settings.editAccessRoleDialog.form.canModifyApprovalGroups")
            }}</label>
          </div>
          <div class="flex items-center gap-1">
            <p-checkbox inputId="canModifyInvitations" [binary]="true" formControlName="canModifyInvitations" />
            <label for="canModifyInvitations" class="ml-2">{{
              t("app.organization-settings.editAccessRoleDialog.form.canModifyInvitations")
            }}</label>
          </div>
          <div class="flex items-center gap-1">
            <p-checkbox inputId="canModifyUsers" [binary]="true" formControlName="canModifyUsers" />
            <label for="canModifyUsers" class="ml-2">{{
              t("app.organization-settings.editAccessRoleDialog.form.canModifyUsers")
            }}</label>
          </div>
          <div class="flex items-center gap-1">
            <p-checkbox inputId="canRemoveUsers" [binary]="true" formControlName="canRemoveUsers" />
            <label for="canRemoveUsers" class="ml-2">{{
              t("app.organization-settings.editAccessRoleDialog.form.canRemoveUsers")
            }}</label>
          </div>
          <div class="flex items-center gap-1">
            <p-checkbox inputId="canModifyAutomationUsers" [binary]="true" formControlName="canModifyAutomationUsers" />
            <label for="canModifyAutomationUsers" class="ml-2">{{
              t("app.organization-settings.editAccessRoleDialog.form.canModifyAutomationUsers")
            }}</label>
          </div>
          <div class="flex items-center gap-1">
            <p-checkbox inputId="canSeeResponsiblePersons" [binary]="true" formControlName="canSeeResponsiblePersons" />
            <label for="canSeeResponsiblePersons" class="ml-2">{{
              t("app.organization-settings.editAccessRoleDialog.form.canSeeResponsiblePersons")
            }}</label>
            @if (form.controls.canViewAllServices.value) {
              <i
                class="pi pi-info-circle ml-1 text-blue-600 dark:text-blue-400"
                [pTooltip]="
                  t('app.organization-settings.editAccessRoleDialog.form.impliedByPermission', {
                    name: t('app.organization-settings.editAccessRoleDialog.form.canViewAllServices'),
                  })
                "
              ></i>
            }
          </div>
          <div class="flex items-center gap-1">
            <p-checkbox inputId="canViewAuditLog" [binary]="true" formControlName="canViewAuditLog" />
            <label for="canViewAuditLog" class="ml-2">{{
              t("app.organization-settings.editAccessRoleDialog.form.canViewAuditLog")
            }}</label>
          </div>
          <!-- TODO(extra-services): remove the `extraServicesEnabled` guard once the feature ships and is always on. -->
          @if (extraServicesEnabled) {
            <div class="flex items-center gap-1">
              <p-checkbox inputId="canViewExtraServices" [binary]="true" formControlName="canViewExtraServices" />
              <label for="canViewExtraServices" class="ml-2">{{
                t("app.organization-settings.editAccessRoleDialog.form.canViewExtraServices")
              }}</label>
              @if (form.controls.canViewAllServices.value) {
                <i
                  class="pi pi-info-circle ml-1 text-blue-600 dark:text-blue-400"
                  [pTooltip]="
                    t('app.organization-settings.editAccessRoleDialog.form.impliedByPermission', {
                      name: t('app.organization-settings.editAccessRoleDialog.form.canViewAllServices'),
                    })
                  "
                ></i>
              } @else if (form.controls.canModifyExtraServices.value) {
                <i
                  class="pi pi-info-circle ml-1 text-blue-600 dark:text-blue-400"
                  [pTooltip]="
                    t('app.organization-settings.editAccessRoleDialog.form.impliedByPermission', {
                      name: t('app.organization-settings.editAccessRoleDialog.form.canModifyExtraServices'),
                    })
                  "
                ></i>
              }
            </div>
            <div class="flex items-center gap-1">
              <p-checkbox inputId="canModifyExtraServices" [binary]="true" formControlName="canModifyExtraServices" />
              <label for="canModifyExtraServices" class="ml-2">{{
                t("app.organization-settings.editAccessRoleDialog.form.canModifyExtraServices")
              }}</label>
            </div>
          }
        </div>

        <p-divider styleClass="my-0" />

        <!-- Process Permissions -->
        <div class="flex flex-col gap-4">
          <p class="text-lg font-medium">
            {{ t("app.organization-settings.editAccessRoleDialog.form.processPermissions") }}
          </p>

          <div class="flex items-center gap-1">
            <p-checkbox inputId="canViewAllServices" [binary]="true" formControlName="canViewAllServices" />
            <label for="canViewAllServices" class="ml-2">{{
              t("app.organization-settings.editAccessRoleDialog.form.canViewAllServices")
            }}</label>
          </div>
          <div class="flex items-center gap-1 ml-8">
            <p-checkbox
              inputId="canViewAllPrivateTransactions"
              [binary]="true"
              formControlName="canViewAllPrivateTransactions"
            />
            <label for="canViewAllPrivateTransactions" class="ml-2">{{
              t("app.organization-settings.editAccessRoleDialog.form.canViewAllPrivateTransactions")
            }}</label>
            @if (!form.controls.canViewAllServices.value) {
              <i
                class="pi pi-info-circle ml-1 text-blue-600 dark:text-blue-400"
                [pTooltip]="
                  t('app.organization-settings.editAccessRoleDialog.form.requiresPermission', {
                    name: t('app.organization-settings.editAccessRoleDialog.form.canViewAllServices'),
                  })
                "
              ></i>
            }
          </div>
          <div class="flex items-center gap-1">
            <p-checkbox inputId="canShareProcesses" [binary]="true" formControlName="canShareProcesses" />
            <label for="canShareProcesses" class="ml-2">{{
              t("app.organization-settings.editAccessRoleDialog.form.canShareProcesses")
            }}</label>
            @if (form.controls.canViewAllServices.value) {
              <i
                class="pi pi-info-circle ml-1 text-blue-600 dark:text-blue-400"
                [pTooltip]="
                  t('app.organization-settings.editAccessRoleDialog.form.impliedByPermission', {
                    name: t('app.organization-settings.editAccessRoleDialog.form.canViewAllServices'),
                  })
                "
              ></i>
            }
          </div>
          <div class="flex items-center gap-1">
            <p-checkbox inputId="canApproveProcesses" [binary]="true" formControlName="canApproveProcesses" />
            <label for="canApproveProcesses" class="ml-2">{{
              t("app.organization-settings.editAccessRoleDialog.form.canApproveProcesses")
            }}</label>
          </div>
          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-1">
              <p-checkbox
                inputId="canAccessEmployeeProcesses"
                [binary]="true"
                formControlName="canAccessEmployeeProcesses"
              />
              <label for="canAccessEmployeeProcesses" class="ml-2">{{
                t("app.organization-settings.editAccessRoleDialog.form.canAccessEmployeeProcesses")
              }}</label>
              @if (form.controls.canViewAllServices.value) {
                <i
                  class="pi pi-info-circle ml-1 text-blue-600 dark:text-blue-400"
                  [pTooltip]="
                    t('app.organization-settings.editAccessRoleDialog.form.impliedByPermission', {
                      name: t('app.organization-settings.editAccessRoleDialog.form.canViewAllServices'),
                    })
                  "
                ></i>
              }
            </div>
            <div
              class="flex gap-3 p-3 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-md"
            >
              <i class="pi pi-info-circle text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"></i>
              <p class="text-sm text-surface-700 dark:text-surface-300">
                {{ t("app.organization-settings.editAccessRoleDialog.form.canAccessEmployeeProcessesInfo") }}
              </p>
            </div>
          </div>

          <p class="text-lg font-medium">
            {{ t("app.organization-settings.editAccessRoleDialog.form.detailedAccessControl") }}
          </p>

          <div class="flex flex-col gap-2">
            <p class="text-sm text-surface-600 dark:text-surface-400">
              {{ t("app.organization-settings.editAccessRoleDialog.form.detailedAccessControlDescription") }}
            </p>

            @if (treeLoading) {
              <div class="flex flex-col gap-2 py-2">
                @for (_ of [1, 2, 3]; track $index) {
                  <p-skeleton height="2rem" borderRadius="6px" />
                }
              </div>
            } @else {
              <p-tree
                class="[--p-tree-background:transparent]"
                [value]="serviceTree"
                [(selection)]="selectedServiceNodes"
                selectionMode="checkbox"
                [propagateSelectionUp]="false"
                (selectionChange)="onServiceTreeSelectionChange()"
              >
                <ng-template let-node pTemplate="default">
                  <div class="flex items-center gap-2">
                    <span>{{ node.label }}</span>
                    @if (node.data?.isVisibleToEmployeeUsers) {
                      <i
                        class="pi pi-info-circle text-blue-600 dark:text-blue-400"
                        [pTooltip]="
                          t('app.organization-settings.editAccessRoleDialog.form.employeeVisibleProcessParentHint')
                        "
                      ></i>
                    }
                  </div>
                </ng-template>
              </p-tree>
            }
          </div>
        </div>

        <!-- RAN Expense Permissions: shown only when the org has an active RAN Expense service. -->
        @if (showRanExpensePermissions) {
          <p-divider styleClass="my-0" />

          <div class="flex flex-col gap-4">
            <p class="text-lg font-medium">
              {{ t("app.organization-settings.editAccessRoleDialog.form.ranExpense.title") }}
            </p>

            <p class="text-sm text-surface-600 dark:text-surface-400">
              {{ t("app.organization-settings.editAccessRoleDialog.form.ranExpense.permissionControlDescription") }}
            </p>

            <div class="flex flex-col gap-3" role="radiogroup">
              @for (option of ranExpenseAccessOptions; track option.value) {
                <div class="flex items-center gap-2">
                  <p-radioButton
                    [inputId]="'ranExpenseAccess_' + option.value"
                    name="ranExpenseAccess"
                    [value]="option.value"
                    formControlName="ranExpenseAccess"
                  />
                  <label [for]="'ranExpenseAccess_' + option.value" class="ml-2">{{ t(option.labelKey) }}</label>
                  @if (option.tooltip) {
                    <i
                      class="pi pi-info-circle ml-1 text-blue-600 dark:text-blue-400"
                      [pTooltip]="t(option.tooltip)"
                      tooltipStyleClass="ran-expense-access-tooltip"
                    ></i>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Footer section - fixed -->
    <div class="mt-3 pt-3 border-t border-surface-200 dark:border-surface-700">
      <div class="flex flex-row justify-between flex-wrap gap-3 w-full">
        <!-- Left-aligned: Delete button -->
        <div>
          <!-- NOTE: Not hiding div around this button, because other buttons would be aligned to the left -->
          @if (!accessRole?.isBuiltInRole && !isCreateMode) {
            <p-button
              [label]="t('app.common.delete')"
              icon="pi pi-trash"
              variant="outlined"
              severity="danger"
              (click)="openConfirmationDeleteDialog()"
              [disabled]="loading"
              [loading]="this.loading"
            />
          }
        </div>
        <!-- Right-aligned: Cancel and Save buttons -->
        <div class="flex gap-3 justify-end">
          <p-button
            [label]="showSaveButton ? t('app.common.cancel') : t('app.common.close')"
            severity="secondary"
            (click)="ref.close()"
            [disabled]="loading"
          ></p-button>
          @if (showSaveButton) {
            <p-button
              [label]="t('app.common.save')"
              (click)="submitEditAccessRoleForm()"
              [disabled]="loading || !canSave"
              [loading]="this.loading"
            ></p-button>
          }
        </div>
      </div>
    </div>
  </div>
</ng-container>
